import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  MapPin,
  ChevronDown,
  X,
  LocateFixed,
  Loader2,
} from 'lucide-react';
import restaurantService, {
  Restaurant,
  RestaurantFilter,
  DEFAULT_RESTAURANT_SEARCH_RADIUS_KM,
} from '../../services/restaurantService';
import { DEFAULT_IMAGES } from '../../constants/images';
import Header from '../Header/Header';
import SeoHead from '../Seo/SeoHead';
import { PepperRating } from './PepperRating';
import { RestaurantListSkeleton } from './RestaurantSkeleton';
import { getRestaurantDisplayRating } from '../../utils/restaurantRating';
import { useSsrPreload, matchSsrPreload } from '../../ssr/preload';
import { trackEvent } from '../../utils/analytics';
import './RestaurantsList.css';

const RATING_FILTER = 'Rating 4.0+';
const GEO_CACHE_KEY = 'peppar_listing_coords';

interface UserCoords {
  lat: number;
  lng: number;
}

function readCachedCoords(): UserCoords | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(GEO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { lat?: number; lng?: number };
    if (typeof parsed.lat === 'number' && typeof parsed.lng === 'number') {
      return { lat: parsed.lat, lng: parsed.lng };
    }
  } catch {
    // Ignore invalid cache
  }
  return null;
}

function writeCachedCoords(coords: UserCoords): void {
  try {
    sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify(coords));
  } catch {
    // Ignore quota / private mode errors
  }
}

function formatAvgCostForTwo(raw: string | undefined, currencySymbol = '₹'): string {
  if (!raw?.trim()) return '';
  const t = raw.trim();
  if (/for\s+two/i.test(t)) return t;
  if (/^\d+(\.\d+)?$/.test(t)) {
    return `${currencySymbol}${t} for two`;
  }
  if (/^[₹$]?\d/.test(t) && !/for\s+two/i.test(t)) {
    return `${t} for two`;
  }
  return t;
}

function RestaurantCard({ restaurant }: { restaurant: Restaurant }) {
  const imageUrl =
    restaurant.coverImage?.trim() ||
    restaurant.restaurantLandingDetailsList?.[0]?.image?.trim() ||
    DEFAULT_IMAGES.RESTAURANT;

  const cuisines = restaurant.cuisineList?.map((c) => c.name).join(' · ') || '';
  const costLabel = formatAvgCostForTwo(
    restaurant.avgCostForTwo,
    restaurant.currencySymbol || '₹'
  );
  const area = restaurant.restaurantAddressDetails?.area;
  const city = restaurant.restaurantAddressDetails?.city;
  const locationLabel = [area, city].filter(Boolean).join(', ');
  const displayRating = getRestaurantDisplayRating(restaurant);

  return (
    <Link to={`/restaurants/${restaurant.appName}`} className="rl-card">
      <div className="rl-card-image-wrap">
        <img
          src={imageUrl}
          alt={restaurant.restaurantName}
          className="rl-card-image"
          onError={(e) => {
            (e.target as HTMLImageElement).src = DEFAULT_IMAGES.RESTAURANT;
          }}
        />
        {displayRating != null && (
          <div className="rl-card-rating">
            <PepperRating value={displayRating} />
          </div>
        )}
      </div>
      <div className="rl-card-body">
        <h3 className="rl-card-title">{restaurant.restaurantName}</h3>
        {cuisines && <p className="rl-card-cuisines">{cuisines}</p>}
        <div className="rl-card-meta">
          {costLabel && <span>{costLabel}</span>}
          {costLabel && restaurant.distanceKm != null && (
            <span className="rl-card-dot" aria-hidden>•</span>
          )}
          {restaurant.distanceKm != null && (
            <span>{restaurant.distanceKm} km</span>
          )}
          {!costLabel && !restaurant.distanceKm && locationLabel && (
            <span>{locationLabel}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

function LocationPrompt({
  locating,
  locateFailed,
  onDetect,
}: {
  locating: boolean;
  locateFailed: boolean;
  onDetect: () => void;
}) {
  return (
    <div className="rl-loc-prompt">
      <div className="rl-loc-illustration" aria-hidden>
        <div className="rl-loc-ring rl-loc-ring-3" />
        <div className="rl-loc-ring rl-loc-ring-2" />
        <div className="rl-loc-ring rl-loc-ring-1" />
        <div className="rl-loc-pin">
          <MapPin size={26} strokeWidth={2.2} />
        </div>
      </div>
      <h2 className="rl-loc-title">
        {locateFailed ? 'Location access denied' : 'Find restaurants near you'}
      </h2>
      <p className="rl-loc-text">
        {locateFailed
          ? "We couldn't access your location. Enable it in your browser settings, or search for a restaurant by name above."
          : 'Share your location to discover restaurants within 20 km, or search by name anywhere on Peppar.'}
      </p>
      {!locateFailed && (
        <button
          type="button"
          className="rl-loc-btn"
          onClick={onDetect}
          disabled={locating}
        >
          {locating ? (
            <>
              <Loader2 size={16} className="rl-spin" />
              Detecting your location…
            </>
          ) : (
            <>
              <LocateFixed size={16} />
              Enable location
            </>
          )}
        </button>
      )}
      {locateFailed && (
        <div className="rl-loc-denied">
          <Search size={14} />
          <span>Use the search bar above to find restaurants</span>
        </div>
      )}
      {!locateFailed && (
        <p className="rl-loc-or">or search by name in the bar above</p>
      )}
    </div>
  );
}

function EmptyState({
  hasSearch,
  onDetectLocation,
  onClearSearch,
}: {
  hasSearch: boolean;
  onDetectLocation: () => void;
  onClearSearch: () => void;
}) {
  return (
    <div className="rl-empty">
      <div className="rl-empty-icon" aria-hidden>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
          <path
            d="M8.5 3.5c1.2-1 2.6-1.4 3.6-.7.7.5.8 1.5.3 2.4 2.6-.3 4.8 1 5.4 3.4.8 3.1-1.1 7.7-4.4 10.4-2.8 2.3-6 2.7-7.9.9-1.8-1.7-1.9-4.8-.3-7.8 1.7-3.2 4.6-5.6 3.3-8.6z"
            fill="var(--rl-ember)"
          />
        </svg>
      </div>
      <h3 className="rl-empty-title">
        {hasSearch ? 'No restaurants match your search' : 'No restaurants nearby'}
      </h3>
      <p className="rl-empty-text">
        {hasSearch
          ? 'Try a different name or clear your search.'
          : 'No restaurants found within 20 km of your location.'}
      </p>
      <div className="rl-empty-actions">
        {!hasSearch && (
          <button type="button" className="rl-btn-detect" onClick={onDetectLocation}>
            <LocateFixed size={14} />
            Retry location
          </button>
        )}
        {hasSearch && (
          <button type="button" className="rl-btn-clear" onClick={onClearSearch}>
            Clear search
          </button>
        )}
      </div>
    </div>
  );
}

const RestaurantsList: React.FC = () => {
  const preload = useSsrPreload();
  const directoryPreload = matchSsrPreload(preload, 'directory') ? preload : null;
  const cachedCoords = readCachedCoords();
  const [restaurants, setRestaurants] = useState<Restaurant[]>(directoryPreload?.restaurants ?? []);
  const [loading, setLoading] = useState(!directoryPreload && !!cachedCoords);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [location, setLocation] = useState(cachedCoords ? 'Current location' : 'Set your location');
  const [coords, setCoords] = useState<UserCoords | null>(cachedCoords);
  const [locating, setLocating] = useState(typeof window !== 'undefined' && !directoryPreload && !cachedCoords);
  const [locateFailed, setLocateFailed] = useState(false);
  const [geoAttempted, setGeoAttempted] = useState(
    typeof window === 'undefined' || !!directoryPreload || !!cachedCoords
  );

  const buildFilter = useCallback((): RestaurantFilter | undefined => {
    const trimmed = query.trim();
    if (trimmed) {
      return { text: trimmed };
    }
    if (coords) {
      return {
        lat: coords.lat,
        lng: coords.lng,
        radiusKm: DEFAULT_RESTAURANT_SEARCH_RADIUS_KM,
      };
    }
    return undefined;
  }, [query, coords]);

  const loadRestaurants = useCallback(async () => {
    const filter = buildFilter();
    if (!filter) {
      if (!directoryPreload?.restaurants?.length) {
        setRestaurants([]);
      }
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await restaurantService.getRestaurants(filter);
      if (response.success) {
        setRestaurants(response.restaurants || []);
      } else {
        setError(response.message || 'Failed to load restaurants');
        setRestaurants([]);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error loading restaurants';
      setError(message);
      setRestaurants([]);
    } finally {
      setLoading(false);
    }
  }, [buildFilter]);

  const handleDetectLocation = useCallback(() => {
    setLocating(true);
    setLocateFailed(false);

    if (!navigator.geolocation) {
      setLocating(false);
      setLocateFailed(true);
      setGeoAttempted(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextCoords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setCoords(nextCoords);
        writeCachedCoords(nextCoords);
        setLocation('Current location');
        setLocating(false);
        setGeoAttempted(true);
      },
      () => {
        setLocating(false);
        setLocateFailed(true);
        setGeoAttempted(true);
      },
      { timeout: 6000 }
    );
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
    // Always attempt geo on mount — SSR preload data is unfiltered and must not
    // be shown until the user's location (or a search query) is confirmed.
    handleDetectLocation();
  }, [handleDetectLocation]);

  useEffect(() => {
    const trimmed = query.trim();
    const filter = buildFilter();
    if (!filter) {
      if (geoAttempted && !locating) {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    const debounceMs = trimmed ? 300 : 0;
    const timer = setTimeout(() => {
      loadRestaurants();
      if (trimmed.length >= 2) {
        trackEvent('search', { search_term: trimmed });
      }
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [buildFilter, query, loadRestaurants, geoAttempted, locating]);

  const toggleFilter = (filter: string) => {
    setActiveFilters((prev) =>
      prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]
    );
  };

  const filtered = useMemo(() => {
    let list = restaurants;
    if (activeFilters.includes(RATING_FILTER)) {
      list = list.filter((r) => {
        const rating = getRestaurantDisplayRating(r);
        return rating != null && rating >= 4;
      });
    }
    return list;
  }, [restaurants, activeFilters]);

  const hasSearch = query.trim().length > 0;

  // Show the location prompt when we have no coords and no search query yet.
  // This covers: waiting for permission, denied, or not yet asked.
  const showLocationPrompt = !hasSearch && !coords;
  // Only show skeleton when actually fetching from the API (filter is active).
  const showSkeleton = loading && (!!coords || hasSearch);
  // Show empty state when a filter is active but returned no results.
  const showEmpty = !loading && !error && !showLocationPrompt && filtered.length === 0;
  const showFeed = !loading && !error && !showLocationPrompt && filtered.length > 0;

  return (
    <div className="rl-page">
      <SeoHead
        title="Restaurants"
        description="Discover restaurants on Peppar — digital menus, table ordering, and more."
        path="/restaurants"
      />
      <Header />
      <div className="rl-header-bar">
        <div className="rl-shell">
          <div className="rl-header">
            <div className="rl-header-top">
              <button type="button" className="rl-location-btn" aria-label="Location">
                <MapPin size={15} className="rl-location-icon" />
                <span className="rl-location-text">{location}</span>
                <ChevronDown size={14} className="rl-location-chevron" />
              </button>
              <button
                type="button"
                onClick={handleDetectLocation}
                disabled={locating}
                className="rl-detect-btn"
              >
                {locating ? (
                  <>
                    <Loader2 size={13} className="rl-spin" />
                    Locating…
                  </>
                ) : (
                  <>
                    <LocateFixed size={13} />
                    Detect
                  </>
                )}
              </button>
            </div>

            <div className="rl-header-controls">
              <div className="rl-search-row">
                <div className="rl-search-wrap">
                  <Search size={16} className="rl-search-icon" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search restaurants or cuisines"
                    className="rl-search-input"
                    aria-label="Search restaurants"
                  />
                </div>
              </div>

              <div className="rl-filters">
                <button
                  type="button"
                  onClick={() => toggleFilter(RATING_FILTER)}
                  className={`rl-filter-chip${activeFilters.includes(RATING_FILTER) ? ' active' : ''}`}
                >
                  {RATING_FILTER}
                  {activeFilters.includes(RATING_FILTER) && <X size={11} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rl-shell">
        {!showLocationPrompt && (
          <div className="rl-page-intro">
            <h1 className="rl-page-title">Restaurants near you</h1>
            <p className="rl-page-subtitle">
              Discover places within 20 km, or search by name anywhere on Peppar.
            </p>
          </div>
        )}

        {!error && !showLocationPrompt && (coords || hasSearch) && (
          <div className="rl-count">
            {loading
              ? 'Searching…'
              : `${filtered.length} restaurant${filtered.length !== 1 ? 's' : ''}${
                  hasSearch ? ' found' : ' near you'
                }`}
          </div>
        )}

        {error && (
          <div className="rl-error">
            <p>{error}</p>
          </div>
        )}

        {showLocationPrompt && (
          <LocationPrompt
            locating={locating}
            locateFailed={locateFailed && geoAttempted}
            onDetect={handleDetectLocation}
          />
        )}

        {showSkeleton && <RestaurantListSkeleton count={6} />}

        {showEmpty && (
          <EmptyState
            hasSearch={hasSearch}
            onDetectLocation={handleDetectLocation}
            onClearSearch={() => setQuery('')}
          />
        )}

        {showFeed && (
          <div className="rl-feed">
            {filtered.map((restaurant) => (
              <RestaurantCard key={restaurant.id} restaurant={restaurant} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RestaurantsList;
